import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  commandsQueryOptions,
  createCommand,
  deleteCommand,
  updateCommand,
  type Command,
  type CommandInput,
} from "../queries/commands-queries";

type Tab = "all" | "personal";
type Mode =
  | { kind: "catalogue" }
  | { kind: "new" }
  | { kind: "edit"; uuid: string }
  | { kind: "view"; command: Command };

export function CommandsView() {
  const { data: commands = [], isLoading } = useQuery(commandsQueryOptions());
  const [mode, setMode] = useState<Mode>({ kind: "catalogue" });

  if (mode.kind === "view") {
    return (
      <CommandBuilder
        mode="edit"
        selected={mode.command}
        readOnly
        onClose={() => setMode({ kind: "catalogue" })}
      />
    );
  }
  if (mode.kind === "new" || mode.kind === "edit") {
    const selected =
      mode.kind === "edit"
        ? commands.find((c) => c.uuid === mode.uuid) ?? null
        : null;
    return (
      <CommandBuilder
        mode={mode.kind}
        selected={selected}
        onClose={() => setMode({ kind: "catalogue" })}
      />
    );
  }

  return (
    <CommandsCatalogue
      commands={commands}
      isLoading={isLoading}
      onCreate={() => setMode({ kind: "new" })}
      onEdit={(uuid) => setMode({ kind: "edit", uuid })}
      onView={(command) => setMode({ kind: "view", command })}
    />
  );
}

interface CatalogueProps {
  commands: Command[];
  isLoading: boolean;
  onCreate: () => void;
  onEdit: (uuid: string) => void;
  onView: (command: Command) => void;
}

function CommandsCatalogue({ commands, isLoading, onCreate, onEdit, onView }: CatalogueProps) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("all");
  const [search, setSearch] = useState("");
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (uuid: string) => deleteCommand(uuid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["commands"] }),
  });

  const filtered = useMemo(() => {
    const scoped = tab === "personal"
      ? commands.filter((c) => c.scope === "personal")
      : commands;
    const q = search.trim().toLowerCase();
    if (!q) return scoped;
    return scoped.filter(
      (c) =>
        c.label.toLowerCase().includes(q) ||
        c.description.toLowerCase().includes(q),
    );
  }, [commands, tab, search]);

  const sectionTitle = tab === "personal" ? "Мои команды" : "Все команды";

  function handleDelete(c: Command) {
    if (!c.uuid) return;
    if (!confirm(`Удалить команду «${c.label}»?`)) return;
    deleteMutation.mutate(c.uuid);
    setOpenMenu(null);
  }

  return (
    <div className="cmd-page">
      <header className="cmd-page-header">
        <h1 className="cmd-page-title">Каталог команд</h1>
        <button
          type="button"
          className="cmd-page-create"
          onClick={onCreate}
        >
          <PlusIcon />
          <span>Создать команду</span>
        </button>
      </header>

      <div className="cmd-page-toolbar">
        <div className="history-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "all"}
            className={`history-tab${tab === "all" ? " active" : ""}`}
            onClick={() => setTab("all")}
          >
            Все
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "personal"}
            className={`history-tab${tab === "personal" ? " active" : ""}`}
            onClick={() => setTab("personal")}
          >
            Личные
          </button>
        </div>
        <div className="cmd-search">
          <SearchIcon />
          <input
            type="search"
            className="cmd-search-input"
            placeholder="Название команды"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <h2 className="cmd-page-section-title">{sectionTitle}</h2>

      {isLoading ? (
        <div className="cmd-empty">Загрузка…</div>
      ) : filtered.length === 0 ? (
        <div className="cmd-empty">
          {search ? (
            <p>Ничего не найдено по запросу «{search}»</p>
          ) : tab === "personal" ? (
            <>
              <p className="cmd-empty-title">Личных команд пока нет</p>
              <p className="cmd-empty-hint">
                Нажмите «Создать команду», чтобы добавить свой шаблон.
              </p>
            </>
          ) : (
            <p>Команд пока нет</p>
          )}
        </div>
      ) : (
        <div className="cmd-grid">
          {filtered.map((c) => (
            <CommandCard
              key={c.uuid ?? `system:${c.id}`}
              command={c}
              menuOpen={openMenu === (c.uuid ?? `system:${c.id}`)}
              onMenuToggle={() => {
                const key = c.uuid ?? `system:${c.id}`;
                setOpenMenu((m) => (m === key ? null : key));
              }}
              onMenuClose={() => setOpenMenu(null)}
              onEdit={() => c.uuid && onEdit(c.uuid)}
              onDelete={() => handleDelete(c)}
              onView={() => onView(c)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface CardProps {
  command: Command;
  menuOpen: boolean;
  onMenuToggle: () => void;
  onMenuClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onView: () => void;
}

function CommandCard({
  command,
  menuOpen,
  onMenuToggle,
  onMenuClose,
  onEdit,
  onDelete,
  onView,
}: CardProps) {
  const isPersonal = command.scope === "personal";
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) onMenuClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onMenuClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen, onMenuClose]);

  return (
    <div className="cmd-card">
      <div className="cmd-card-head">
        <span className="cmd-card-name">
          <span className="cmd-card-name-slash">/</span>
          {" "}
          {command.label || command.id}
        </span>
        <div className="cmd-card-menu-wrap" ref={wrapRef}>
          <button
            type="button"
            className="cmd-card-kebab"
            onClick={onMenuToggle}
            aria-label="Действия"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <KebabIcon />
          </button>
          {menuOpen && (
            <div className="cmd-card-menu" role="menu">
              {isPersonal ? (
                <>
                  <button
                    type="button"
                    role="menuitem"
                    className="cmd-card-menu-item"
                    onClick={() => { onMenuClose(); onEdit(); }}
                  >
                    <EditIcon />
                    <span>Редактировать</span>
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="cmd-card-menu-item cmd-card-menu-item--danger"
                    onClick={() => { onMenuClose(); onDelete(); }}
                  >
                    <TrashIcon />
                    <span>Удалить</span>
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  role="menuitem"
                  className="cmd-card-menu-item"
                  onClick={() => { onMenuClose(); onView(); }}
                >
                  <EyeIcon />
                  <span>Открыть</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      <p className="cmd-card-desc">{command.description || "Без описания"}</p>
    </div>
  );
}

interface BuilderProps {
  mode: "new" | "edit";
  selected: Command | null;
  onClose: () => void;
  readOnly?: boolean;
}

interface FormState {
  label: string;
  description: string;
  template: string;
}

const EMPTY_FORM: FormState = { label: "", description: "", template: "" };

function CommandBuilder({ mode, selected, onClose, readOnly = false }: BuilderProps) {
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(() => {
    if (mode === "edit" && selected) {
      return {
        label: selected.label,
        description: selected.description,
        template: selected.template,
      };
    }
    return EMPTY_FORM;
  });
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["commands"] });

  const createMutation = useMutation({
    mutationFn: (input: CommandInput) => createCommand(input),
    onSuccess: () => { invalidate(); onClose(); },
    onError: (e: Error) => setError(humanize(e.message)),
  });
  const updateMutation = useMutation({
    mutationFn: ({ uuid, input }: { uuid: string; input: CommandInput }) =>
      updateCommand(uuid, input),
    onSuccess: () => { invalidate(); onClose(); },
    onError: (e: Error) => setError(humanize(e.message)),
  });

  const isPending = createMutation.isPending || updateMutation.isPending;
  const canSave =
    form.label.trim().length > 0 &&
    form.template.trim().length > 0 &&
    !isPending;

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setError(null);
  }

  function handleSave() {
    setError(null);
    if (!form.label.trim()) {
      setError("Имя команды не может быть пустым.");
      return;
    }
    if (!form.template.trim()) {
      setError("Инструкции не могут быть пустыми.");
      return;
    }
    const input: CommandInput = {
      label: form.label.trim(),
      description: form.description.trim(),
      template: form.template,
    };
    if (mode === "new") createMutation.mutate(input);
    else if (selected?.uuid) updateMutation.mutate({ uuid: selected.uuid, input });
  }

  const title = mode === "new" ? "Новая команда" : selected?.label || "Команда";

  return (
    <div className="cmd-builder">
      <header className="cmd-builder-topbar">
        <button
          type="button"
          className="cmd-builder-back"
          onClick={onClose}
          aria-label="Назад"
        >
          <ArrowLeftIcon />
        </button>
        <span className="cmd-builder-topbar-title">{title}</span>
      </header>

      <div className="cmd-builder-body">
        <p className="cmd-builder-intro">
          Команда — это заранее заготовленная инструкция или промпты, которые запускают определённый сценарий.
        </p>

        {error && <div className="cmd-builder-error">{error}</div>}

        <div className="cmd-builder-fields">
          <Field label="Имя команды">
            <input
              type="text"
              className="cmd-builder-input"
              value={form.label}
              onChange={(e) => update("label", e.target.value)}
              placeholder="Напишите имя команды"
              maxLength={120}
              autoComplete="off"
              readOnly={readOnly}
            />
          </Field>

          <Field label="Описание">
            <input
              type="text"
              className="cmd-builder-input"
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              placeholder="Напишите описание команды"
              maxLength={240}
              readOnly={readOnly}
            />
          </Field>

          <Field label="Инструкции">
            <textarea
              className="cmd-builder-textarea"
              value={form.template}
              onChange={(e) => update("template", e.target.value)}
              placeholder="Придумайте инструкции для команды"
              spellCheck={false}
              readOnly={readOnly}
            />
          </Field>
        </div>

        {!readOnly && (
          <div className="cmd-builder-actions">
            <button
              type="button"
              className="cmd-page-create"
              onClick={handleSave}
              disabled={!canSave}
            >
              Сохранить
            </button>
            <button
              type="button"
              className="cmd-builder-cancel"
              onClick={onClose}
            >
              Отмена
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="cmd-builder-field">
      <span className="cmd-builder-field-label">{label}</span>
      {children}
    </label>
  );
}

function humanize(msg: string): string {
  const m = msg.match(/"detail":"([^"]+)"/);
  if (m && m[1]) return m[1];
  return msg.slice(0, 200);
}

function PlusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="m11 11 3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function KebabIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <circle cx="8" cy="3.5" r="1.4" />
      <circle cx="8" cy="8" r="1.4" />
      <circle cx="8" cy="12.5" r="1.4" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M8 3.5C4.5 3.5 1.83 6.04 1 8c.83 1.96 3.5 4.5 7 4.5s6.17-2.54 7-4.5c-.83-1.96-3.5-4.5-7-4.5Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M14.1666 3.21354C14.1667 2.84774 14.021 2.49697 13.7623 2.23828C13.5038 1.9797 13.1534 1.83411 12.7877 1.83398C12.4221 1.83394 12.0711 1.97918 11.8125 2.23763L10.7116 3.33854L12.6621 5.28906L13.7623 4.1888C14.021 3.9302 14.1666 3.5793 14.1666 3.21354ZM2.91469 11.1374C2.81831 11.2335 2.74583 11.3517 2.7057 11.4818L2.70635 11.4824L1.91599 14.0833L4.51885 13.2943C4.64934 13.2546 4.76861 13.1835 4.86521 13.0872L11.955 5.99609L10.0045 4.04557L2.91469 11.1374ZM15.1666 3.21354C15.1666 3.80518 14.9461 4.37413 14.5508 4.81055L14.4694 4.89583L13.069 6.29557C13.0541 6.31581 13.0385 6.33586 13.0202 6.35417C13.0018 6.37247 12.9818 6.38812 12.9616 6.40299L5.57094 13.7956C5.38501 13.9809 5.16184 14.1242 4.91664 14.2155L4.80987 14.2513L1.90752 15.1315H1.90622C1.76223 15.1747 1.60931 15.1786 1.46351 15.1419C1.31762 15.1052 1.18456 15.0295 1.0781 14.9232C0.971689 14.8169 0.895629 14.6842 0.858696 14.5384C0.821783 14.3926 0.825387 14.2392 0.868462 14.0951L0.869113 14.0931L1.74932 11.1914L1.74997 11.1901C1.83808 10.9029 1.99556 10.6418 2.20831 10.4297L9.59698 3.03841C9.61183 3.01821 9.62818 2.99874 9.64646 2.98047C9.66473 2.9622 9.6842 2.94584 9.7044 2.93099L11.1048 1.5306C11.551 1.08446 12.1567 0.833905 12.7877 0.833984C13.4187 0.834109 14.0239 1.08504 14.47 1.53125C14.9161 1.97749 15.1667 2.58261 15.1666 3.21354Z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" clipRule="evenodd" d="M9.33301 0.832031C9.82896 0.832031 10.2833 1.07533 10.6035 1.39551C10.9237 1.71568 11.167 2.17007 11.167 2.66602V3.49902H14C14.2761 3.49902 14.5 3.72288 14.5 3.99902C14.5 4.27517 14.2761 4.49902 14 4.49902H13.167V13.332C13.167 13.828 12.9237 14.2824 12.6035 14.6025C12.2833 14.9227 11.829 15.166 11.333 15.166H4.66699C4.17104 15.166 3.71666 14.9227 3.39648 14.6025C3.07631 14.2824 2.83301 13.828 2.83301 13.332V4.49902H2C1.72386 4.49902 1.5 4.27517 1.5 3.99902C1.5 3.72288 1.72386 3.49902 2 3.49902H4.83301V2.66602C4.83301 2.17007 5.07631 1.71568 5.39648 1.39551C5.71666 1.07533 6.17104 0.832031 6.66699 0.832031H9.33301ZM3.83301 13.332C3.83301 13.5027 3.92369 13.7157 4.10352 13.8955C4.28334 14.0753 4.49627 14.166 4.66699 14.166H11.333C11.5037 14.166 11.7167 14.0753 11.8965 13.8955C12.0763 13.7157 12.167 13.5027 12.167 13.332V4.49902H3.83301V13.332ZM6.66699 6.83203C6.94288 6.83221 7.16682 7.05615 7.16699 7.33203V11.332C7.16699 11.6081 6.94299 11.8319 6.66699 11.832C6.39085 11.832 6.16699 11.6082 6.16699 11.332V7.33203C6.16717 7.05604 6.39096 6.83203 6.66699 6.83203ZM9.33301 6.83203C9.60904 6.83203 9.83283 7.05604 9.83301 7.33203V11.332C9.83301 11.6082 9.60915 11.832 9.33301 11.832C9.05702 11.8319 8.83301 11.6081 8.83301 11.332V7.33203C8.83318 7.05615 9.05712 6.83221 9.33301 6.83203ZM6.66699 1.83203C6.49627 1.83203 6.28334 1.92271 6.10352 2.10254C5.92369 2.28237 5.83301 2.4953 5.83301 2.66602V3.49902H10.167V2.66602C10.167 2.4953 10.0763 2.28237 9.89648 2.10254C9.71666 1.92271 9.50373 1.83203 9.33301 1.83203H6.66699Z" />
    </svg>
  );
}

function ArrowLeftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M10 3 5 8l5 5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
